import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { faFileLines } from "@fortawesome/free-solid-svg-icons/faFileLines";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { ConversationMessage } from "../../../../../model/integration/dto/meta/MetaConversationMessage";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";
import { getMediaUrl } from "../../../../../utils/Media";
import { WAMediaRetentionInfo } from "./WAMediaRetentionInfo";
import { WAMediaUnavailable } from "./WAMediaUnavailable";
import { isMediaPending } from "./media-state";

const Chip = styled.a`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  max-width: 17rem;
  padding: ${Spacing.xs} 0;
  text-decoration: none;
  color: inherit;

  &:hover .atira-wa-doc-download {
    opacity: 1;
  }
`;

const IconWell = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border-radius: ${Rounded.md};
  background-color: rgba(128, 128, 128, 0.2);
`;

const Name = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const DownloadIcon = styled(Flex)`
  margin-inline-start: auto;
  flex-shrink: 0;
  opacity: 0.6;
  transition: opacity 0.15s ease;
`;

type Props = {
  message: ConversationMessage;
};

export const WAMessageDocument: React.FC<Props> = ({ message }) => {
  const { mediaId, document, localPreviewUrl } = message;
  const { t } = useTranslation();

  const filename = document?.filename || "Document";

  if (!mediaId && !localPreviewUrl) {
    return (
      <WAMediaUnavailable
        label={filename}
        expired={Boolean(message.mediaExpiredAt)}
        pending={isMediaPending(message)}
      />
    );
  }

  const extension = filename.includes(".")
    ? filename.split(".").pop()?.toUpperCase()
    : undefined;

  return (
    <Chip
      href={
        localPreviewUrl ||
        getMediaUrl(mediaId!, { name: filename, download: 1 })
      }
      download={filename}
      title={`${t("common.download")} — ${filename}`}
    >
      <IconWell>
        <AtiraIcon icon={faFileLines} />
      </IconWell>

      <Flex flexDirection="column" minWidth="0" flex={1}>
        <Name fontSize="s">{filename}</Name>
        {extension ? (
          <Text fontSize="xs" style={{ opacity: 0.7 }}>
            {extension}
          </Text>
        ) : null}
      </Flex>

      {mediaId ? (
        <WAMediaRetentionInfo createdAt={message.createdAt} variant="inline" />
      ) : null}

      <DownloadIcon className="atira-wa-doc-download">
        <AtiraIcon icon={faDownload} size="sm" />
      </DownloadIcon>
    </Chip>
  );
};
